export const IPV4_MASK = {
  mask: [/\d/, /\d?/, /\d?/, '.', /\d/, /\d?/, /\d?/, '.', /\d/, /\d?/, /\d?/, '.', /\d/, /\d?/, /\d?/],
  guide: false,
  placeholderChar: '_',
  pipe: (conformedValue: string | undefined) => {
    conformedValue = conformedValue?.replaceAll('..', '.');
    conformedValue = conformedValue?.replaceAll('_', '');
    return conformedValue;
  },
  keepCharPositions: true,
  showMask: false
};

/** Default max number of digits accepted after the `/` of an IPv4 CIDR value.
 * CIDR (Classless Inter-Domain Routing) is the standard way to write an IP address together with the size of its network, as address/prefix:
 * 192.168.1.10/24
 * └─ address ─┘└┬┘
 *               prefix length = number of leading bits that identify the network
 * An IPv4 address is 32 bits, written as 4 octets (0–255).
 * /24 means the first 24 bits are the network (192.168.1.x) and the last 8 bits are the host, so 256 addresses.
 * /24 is the same as the older subnet-mask notation 255.255.255.0, and /8 is the same as 255.0.0.0.
 * Valid IPv4 prefixes go from /0 to /32.
 * "Masked IPv4 CIDR value" in mask-utils.ts means the raw text inside the input after the text-mask directive has formatted it. Two different "masks" are involved here:
 * Term
 * Meaning
 * Input mask (text-mask)
 * The typing template ___.___.___.___/___ that limits which characters go where
 * Network mask (CIDR prefix)
 * The /24 part of the address
 * Because guide: true, text-mask fills the empty slots with _, so the form control holds something like 10.0.0.1/8__. cleanIpv4CidrValue removes those _ to get the real CIDR value 10.0.0.1/8 before it goes into the payload.
 * Two things to know:
 * Prefix range: the input allows up to 3 digits after /, as the story asked, so /999 passes. A real IPv4 prefix stops at /32. Capping it at 32 would only be a change in ipv4WithMaskValidator.
 * Out-of-date comment: the doc comment on buildIpv4CidrMask still says "showMask displays ___.___.___.___/___ when empty", but showMask is now false and the dialog's placeholder shows that text. I can update the comment if you want.
 * */
export const IPV4_CIDR_DEFAULT_MAX_MASK_LENGTH = 3;

const IPV4_OCTETS_COUNT = 4;
const IPV4_OCTET_MAX_LENGTH = 3;
const IPV4_PLACEHOLDER_CHAR = '_';
const DIGIT = /\d/;
const SEPARATOR = /[./]/;
/** text-mask caret trap: after a deletion the caret stops at the start of the octet instead of jumping before the separator. */
const CARET_TRAP = '[]';

/** text-mask state received by the mask function (2nd argument) and the pipe. */
export interface Ipv4CidrMaskState {
  rawValue?: string;
  currentCaretPosition?: number;
  previousConformedValue?: string;
}

interface ParsedIpv4Cidr {
  octets: string[];
  cidr: string;
  invalid: boolean;
}

const digitSlots = (count: number): RegExp[] => Array.from({ length: Math.max(count, 1) }, () => DIGIT);
const onlyDigits = (value: string | undefined): string => `${value ?? ''}`.replace(/\D/g, '');
const removeCharAt = (value: string, index: number): string => value.slice(0, index) + value.slice(index + 1);

/**
 * Positional parsing of the input value: octet N is always the text between the (N-1)th and Nth `.`, so editing
 * one octet never shifts the digits of the others.
 * - An octet without placeholder followed by a separator is "closed": it only takes its digits (`10.`).
 * - Other octets take 3 slots, digits left-aligned (`1__`).
 * - `.` typed inside an octet closes it (`.` in the last octet acts as `/`), `/` closes the last octet.
 * - A 4th digit typed at the end of a full octet moves to the next octet.
 * - Deleting a separator is ignored, overflowing octets / mask part are flagged as `invalid`.
 */
const parseIpv4Cidr = (rawValue: string | undefined, caret: number, maxMaskLength: number, previousValue?: string): ParsedIpv4Cidr => {
  let value = `${rawValue ?? ''}`;
  if (
    previousValue &&
    value.length === previousValue.length - 1 &&
    SEPARATOR.test(previousValue[caret] ?? '') &&
    removeCharAt(previousValue, caret) === value
  ) {
    return { ...parseIpv4Cidr(previousValue, -1, maxMaskLength), invalid: true };
  }
  const typedIndex = caret - 1;
  const typedChar = typedIndex >= 0 ? value[typedIndex] : undefined;
  const octetIndex = value.slice(0, Math.max(typedIndex, 0)).split('.').length - 1;
  const firstSlashIndex = value.indexOf('/');
  const typedInAddress = typedChar !== undefined && (firstSlashIndex === -1 || typedIndex <= firstSlashIndex);
  if (typedInAddress && typedChar === '.' && octetIndex === IPV4_OCTETS_COUNT - 1) {
    value = `${value.slice(0, typedIndex)}/${value.slice(caret)}`;
  }
  const slashIndex = value.indexOf('/');
  const parts = (slashIndex === -1 ? value : value.slice(0, slashIndex)).split('.');
  let cidrText = slashIndex === -1 ? '' : value.slice(slashIndex + 1);
  if (
    typedInAddress &&
    typedChar === '.' &&
    parts.length > IPV4_OCTETS_COUNT &&
    DIGIT.test(parts[octetIndex] ?? '') &&
    !DIGIT.test(parts[octetIndex + 1] ?? '')
  ) {
    // The typed `.` closes the octet: drop the placeholders remaining after it.
    parts.splice(octetIndex + 1, 1);
  }
  if (typedInAddress && SEPARATOR.test(typedChar) && (parts.length !== IPV4_OCTETS_COUNT || !DIGIT.test(parts[octetIndex] ?? ''))) {
    // The typed separator can't close anything: ignore it.
    return parseIpv4Cidr(removeCharAt(value, typedIndex), -1, maxMaskLength);
  }
  if (typedInAddress && DIGIT.test(typedChar) && onlyDigits(parts[octetIndex]).length > IPV4_OCTET_MAX_LENGTH) {
    const part = parts[octetIndex];
    const offset = typedIndex - value.lastIndexOf('.', typedIndex - 1) - 1;
    if (!DIGIT.test(part.slice(offset + 1))) {
      parts[octetIndex] = removeCharAt(part, offset);
      if (octetIndex < IPV4_OCTETS_COUNT - 1) {
        parts[octetIndex + 1] = `${typedChar}${parts[octetIndex + 1] ?? ''}`;
      } else {
        cidrText = `${typedChar}${cidrText}`;
      }
    }
  }
  const octets = Array.from({ length: IPV4_OCTETS_COUNT }, (_, index) => {
    const part = parts[index] ?? '';
    const digits = onlyDigits(part);
    const closed = !!digits && !part.includes(IPV4_PLACEHOLDER_CHAR) && (index < parts.length - 1 || slashIndex !== -1);
    return closed ? digits : digits.padEnd(IPV4_OCTET_MAX_LENGTH, IPV4_PLACEHOLDER_CHAR);
  });
  const cidrLength = Math.max(maxMaskLength, 1);
  const cidr = onlyDigits(cidrText).padEnd(cidrLength, IPV4_PLACEHOLDER_CHAR);
  return {
    octets: octets.map((octet) => octet.slice(0, IPV4_OCTET_MAX_LENGTH)),
    cidr: cidr.slice(0, cidrLength),
    invalid: parts.length > IPV4_OCTETS_COUNT || octets.some((octet) => octet.length > IPV4_OCTET_MAX_LENGTH) || cidr.length > cidrLength
  };
};

/**
 * Dynamic text-mask function for an IPv4 address with a CIDR mask (e.g. `192.168.1.10/24`), compatible with `guide` / `showMask`.
 * Every octet displays 3 slots (`___.___.___.___/___`) until the user "closes" it by typing `.` (or `/` for the last one):
 * the octet then shrinks to its typed digits and the caret moves to the next octet.
 * Must be used with {@link buildIpv4CidrPipe} (see {@link buildIpv4CidrMask}).
 */
export const buildIpv4CidrMaskFn =
  (maxMaskLength: number = IPV4_CIDR_DEFAULT_MAX_MASK_LENGTH) =>
  (rawValue: string, state?: Ipv4CidrMaskState): (string | RegExp)[] => {
    const { octets } = parseIpv4Cidr(rawValue, state?.currentCaretPosition ?? -1, maxMaskLength, state?.previousConformedValue);
    const mask: (string | RegExp)[] = [];
    octets.forEach((octet, index) => {
      if (index > 0) {
        mask.push('.', CARET_TRAP);
      }
      mask.push(...digitSlots(octet.length));
    });
    return [...mask, '/', CARET_TRAP, ...digitSlots(maxMaskLength)];
  };

/**
 * text-mask pipe paired with {@link buildIpv4CidrMaskFn}: rebuilds the value positionally from the raw input
 * (text-mask default conforming shifts characters across octets when their size changes) and rejects overflowing input.
 */
export const buildIpv4CidrPipe =
  (maxMaskLength: number = IPV4_CIDR_DEFAULT_MAX_MASK_LENGTH) =>
  (_conformedValue: string, state: Ipv4CidrMaskState): string | false => {
    const { octets, cidr, invalid } = parseIpv4Cidr(state?.rawValue, state?.currentCaretPosition ?? -1, maxMaskLength, state?.previousConformedValue);
    return invalid && state?.previousConformedValue ? false : `${octets.join('.')}/${cidr}`;
  };

/** Removes the text-mask placeholder chars (`_`) from a masked IPv4 CIDR value (e.g. `10.0.0.1/8__` → `10.0.0.1/8`). */
export const cleanIpv4CidrValue = (value: string | null | undefined): string => `${value ?? ''}`.replace(/_/g, '').trim();

/** Input placeholder showing the expected format, e.g. `___.___.___.___/___`. */
export const buildIpv4CidrPlaceholder = (maxMaskLength: number = IPV4_CIDR_DEFAULT_MAX_MASK_LENGTH): string =>
  `___.___.___.___/${'_'.repeat(Math.max(maxMaskLength, 1))}`;

/**
 * Text-mask configuration for an IPv4 address with a CIDR mask of 1..`maxMaskLength` digits (e.g. `192.168.1.10/24`).
 * `showMask` displays `___.___.___.___/___` when empty, `guide` keeps it displayed while typing.
 * The input value then contains `_` placeholders: use {@link cleanIpv4CidrValue} before using it.
 * `keepCharPositions` is disabled: the pipe keeps every octet in place itself.
 */
export const buildIpv4CidrMask = (maxMaskLength: number = IPV4_CIDR_DEFAULT_MAX_MASK_LENGTH) => ({
  mask: buildIpv4CidrMaskFn(maxMaskLength),
  pipe: buildIpv4CidrPipe(maxMaskLength),
  guide: true,
  placeholderChar: IPV4_PLACEHOLDER_CHAR,
  keepCharPositions: false,
  showMask: true
});

/** IPv4 + CIDR mask (max 3 digits) text-mask configuration. */
export const IPV4_CIDR_MASK = buildIpv4CidrMask();
